import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { themeConfig } from '../utils/pastelThemes';
import '../styles/Onboarding.css';

const Onboarding = () => {
    const navigate = useNavigate();
    const { changeTheme, currentTheme } = useTheme();
    const [step, setStep] = useState(1);
    const [userInfo, setUserInfo] = useState({
        nickname: '',
        interests: [],
        theme: currentTheme
    });

    const interests = [
        { id: 'soccer', label: '축구', emoji: '⚽' },
        { id: 'game', label: '게임', emoji: '🎮' },
        { id: 'pokemon', label: '포켓몬', emoji: '🎯' },
        { id: 'music', label: '음악', emoji: '🎵' },
        { id: 'art', label: '미술', emoji: '🎨' },
        { id: 'science', label: '과학', emoji: '🔬' }
    ];

    const handleNicknameSubmit = (e) => {
        e.preventDefault();
        if (userInfo.nickname.trim()) {
            setStep(2);
        }
    };

    const toggleInterest = (interestId) => {
        setUserInfo(prev => ({
            ...prev,
            interests: prev.interests.includes(interestId)
                ? prev.interests.filter(id => id !== interestId)
                : [...prev.interests, interestId]
        }));
    };

    const handleThemeSelect = (themeName) => {
        setUserInfo(prev => ({ ...prev, theme: themeName }));
        changeTheme(themeName);
    };

    const handleComplete = () => {
        const completeUserInfo = {
            ...userInfo,
            completedOnboarding: true,
            createdAt: new Date().toISOString()
        };
        localStorage.setItem('userInfo', JSON.stringify(completeUserInfo));
        navigate('/courses');
    };

    return (
        <div className="onboarding-container">
            <div className="onboarding-card">
                {/* Step 1: 닉네임 입력 */}
                {step === 1 && (
                    <div className="step-content">
                        <h1 className="step-title">안녕하세요! 👋</h1>
                        <p className="step-description">
                            코딩 모험을 시작하기 전에 이름을 알려주세요!
                        </p>
                        <form onSubmit={handleNicknameSubmit}>
                            <input
                                type="text"
                                className="nickname-input"
                                placeholder="닉네임을 입력하세요"
                                value={userInfo.nickname}
                                onChange={(e) => setUserInfo({ ...userInfo, nickname: e.target.value })}
                                maxLength={20}
                                autoFocus
                            />
                            <button
                                type="submit"
                                className="next-button"
                                disabled={!userInfo.nickname.trim()}
                            >
                                다음으로 →
                            </button>
                        </form>
                    </div>
                )}

                {/* Step 2: 관심사 선택 */}
                {step === 2 && (
                    <div className="step-content">
                        <h1 className="step-title">무엇을 좋아하나요? 🌟</h1>
                        <p className="step-description">
                            {userInfo.nickname}님의 관심사를 선택해주세요! (여러 개 선택 가능)
                        </p>
                        <div className="interests-grid">
                            {interests.map(interest => (
                                <button
                                    key={interest.id}
                                    className={`interest-card ${userInfo.interests.includes(interest.id) ? 'selected' : ''}`}
                                    onClick={() => toggleInterest(interest.id)}
                                >
                                    <span className="interest-emoji">{interest.emoji}</span>
                                    <span className="interest-label">{interest.label}</span>
                                </button>
                            ))}
                        </div>
                        <button
                            className="next-button"
                            onClick={() => setStep(3)}
                            disabled={userInfo.interests.length === 0}
                        >
                            다음으로 →
                        </button>
                    </div>
                )}

                {/* Step 3: 테마 선택 */}
                {step === 3 && (
                    <div className="step-content">
                        <h1 className="step-title">좋아하는 색상을 선택하세요! 🎨</h1>
                        <p className="step-description">
                            학습 화면의 테마를 선택해주세요
                        </p>
                        <div className="themes-grid">
                            {Object.entries(themeConfig).map(([key, config]) => (
                                <button
                                    key={key}
                                    className={`theme-card ${userInfo.theme === key ? 'selected' : ''}`}
                                    onClick={() => handleThemeSelect(key)}
                                    style={{
                                        background: `var(--primary-gradient)`,
                                        '--primary-gradient': `linear-gradient(135deg, var(--${key}-primary), var(--${key}-primary-light))`
                                    }}
                                >
                                    <span className="theme-emoji">{config.emoji}</span>
                                    <span className="theme-name">{config.name}</span>
                                    <span className="theme-description">{config.description}</span>
                                </button>
                            ))}
                        </div>
                        <div className="button-group">
                            <button className="back-button" onClick={() => setStep(2)}>
                                ← 이전
                            </button>
                            <button className="complete-button" onClick={handleComplete}>
                                시작하기! 🚀
                            </button>
                        </div>
                    </div>
                )}

                {/* Progress Indicator */}
                <div className="progress-indicator">
                    {[1, 2, 3].map(i => (
                        <div
                            key={i}
                            className={`progress-dot ${i <= step ? 'active' : ''}`}
                        />
                    ))}
                </div>
            </div>
        </div>
    );
};

export default Onboarding;